import { useEffect } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import {
    teams as recoilStoreTeams,
    draftTeams as recoilStoreDraftTeams,
    sspSubmitOrder,
    initializedTeamKey,
    draftTeams,
    teamValidationCache,
} from 'pages/provisioning/day2/github-onboarding-new/store';
import { COMMON } from 'pages/provisioning/day2/github-onboarding-new/utils/constant';
import { getDefaultTeams, isReaderTeamName, updateTeamNames } from 'pages/provisioning/day2/github-onboarding-new/utils';

const useInitializeTeamsState = () => {
    const order = useRecoilValue(sspSubmitOrder);
    const teamsValue = useRecoilValue(recoilStoreTeams);
    const draftTeamsValue = useRecoilValue(draftTeams);
    const { capability, capabilityDetails, businessApplication, isReaderTeamRequired } = order || {};

    const lastInitializedKey = useRecoilValue(initializedTeamKey);

    const setTeams = useSetRecoilState(recoilStoreTeams);
    const setDraftTeams = useSetRecoilState(recoilStoreDraftTeams);
    const setInitializedKey = useSetRecoilState(initializedTeamKey);
    const setValidationCache = useSetRecoilState(teamValidationCache);

    const currentKey = `${capability}-${businessApplication}`;

    useEffect(() => {
        const hasExistingTeams =
            teamsValue.length > 0 ||
            draftTeamsValue.some(
                (team) =>
                    (team.members && team.members.length > 0) ||
                    (team.originalMembers && team.originalMembers.length > 0) ||
                    (team.repos && team.repos.length > 0)
            );

        const shouldInitialize = capability && businessApplication && currentKey !== lastInitializedKey && !hasExistingTeams;

        if (shouldInitialize) {
            const defaultTeams = getDefaultTeams(capability, capabilityDetails, businessApplication, isReaderTeamRequired);
            setTeams(defaultTeams);
            setDraftTeams(defaultTeams);
            setInitializedKey(currentKey);
            setValidationCache({});
        } else if (capability && capabilityDetails && businessApplication && currentKey !== lastInitializedKey && hasExistingTeams) {
            // sync default team names if capability or business application has changed (but preserve data)
            setTeams((prev) => updateTeamNames(prev, capability, capabilityDetails, businessApplication));
            setDraftTeams((prev) => updateTeamNames(prev, capability, capabilityDetails, businessApplication));
            setInitializedKey(currentKey);
            setValidationCache({});
        } else if (capability && capabilityDetails && businessApplication && currentKey === lastInitializedKey) {
            // isReaderTeamRequired toggled: add or remove the reader team without touching other teams
            const readerTeamTemplate = getDefaultTeams(capability, capabilityDetails, businessApplication, COMMON.YES)[0];
            const applyReaderToggle = (prev) => {
                if (!prev?.length) return prev;
                const hasReader = prev.some((t) => t?.isDefault && isReaderTeamName(t?.name));
                if (isReaderTeamRequired === COMMON.YES && !hasReader) return [readerTeamTemplate, ...prev];
                if (isReaderTeamRequired !== COMMON.YES && hasReader) return prev.filter((t) => !(t?.isDefault && isReaderTeamName(t?.name)));
                return prev;
            };
            setTeams((prev) => applyReaderToggle(prev));
            setDraftTeams((prev) => applyReaderToggle(prev));
        }
    }, [capability, businessApplication, isReaderTeamRequired, currentKey, lastInitializedKey, teamsValue.length, draftTeamsValue, capabilityDetails]);
};

export default useInitializeTeamsState;
